import { useMemo, useState } from 'react';
import { FileText, Check, MessageSquare, AlertTriangle, Clock, Eye, PackagePlus, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { TRIAGEM_TH, TRIAGEM_LARGURA_MINIMA_PADRAO } from '@/components/kanban/triagem/triagemTabela';
import { useTriagemAtivacao, type ItemTriagemAtivacao } from './useTriagemAtivacao';
import { useFinalizarPedidoAtivo } from './useFinalizarPedidoAtivo';
import { NumeroLinhaHaDialog } from './NumeroLinhaHaDialog';
import { TriagemAtivacaoRow } from './TriagemAtivacaoRow';
import { TriagemStandardHeaders, TriagemStandardColGroup } from './TriagemStandardColumns';
import { PedidoComplementarDialog } from './PedidoComplementarDialog';
import { linhasDaCotacao, type AlvoComplementar } from './usePedidoComplementar';

interface TriagemNotaFiscalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemNotaFiscalModal({ isOpen, onClose }: TriagemNotaFiscalModalProps) {
  const {
    itens: itensTodos, isLoading, refetch,
    moverParaSmsEnviado, moverParaCpfCnpjDivergente, moverParaFinal,
  } = useTriagemAtivacao('AGUARDANDO NOTA FISCAL');
  /* Só a cotação clicada fica ocupada; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). O Finalizar é da linha. */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const chave = (i: ItemTriagemAtivacao, acao: string) =>
    `${acao === 'finalizar' ? i.item_id : i.numero_cotacao_operadora || i.item_id}|${acao}`;
  const rodar = (i: ItemTriagemAtivacao, acao: string, fn: () => Promise<unknown>) => {
    void ocupar(chave(i, acao), fn).catch(() => undefined);
  };
  /* O Pedido complementar abre a mesma janela da Triagem SMS (Douglas
     29/10/2026): chamado e texto obrigatórios, a cotação anda inteira. */
  const [complementar, setComplementar] = useState<AlvoComplementar | null>(null);

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );

  const { finalizarPedidoAsync, numeroHaDialog } = useFinalizarPedidoAtivo();
  const uniquePedidos = new Set(itens.map((i) => i.pedido_id)).size;
  // Fila que acumula sem explicação é fila que ninguém consegue cobrar.
  const semJustificativa = itens.filter((i) => !i.fila_justificativa).length;

  const handleFinalizar = (item: typeof itens[0]) => {
    rodar(item, 'finalizar', () => finalizarPedidoAsync({
      item_id: item.item_id,
      pedido_id: item.pedido_id,
      cnpj: item.cnpj,
      razao_social: item.razao_social,
      telefone: item.telefone,
      email: item.email,
      cliente_id: item.cliente_id,
      status_anterior_id: item.status_id,
      coluna_anterior: item.coluna_atual,
      quadro_anterior: item.quadro_atual,
      acao: 'finalizar_ativo',
    }));
  };

  return (
    <>
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={FileText}
      titulo="Triagem Nota Fiscal / Bilhete"
      contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: 0 }}
      cabecalhoExtra={<span className="whitespace-nowrap rounded-lg border border-border bg-card px-3 py-1 text-[11px] text-foreground">{semJustificativa} sem justificativa</span>}
      segmento={segmento}
      onSegmento={setSegmento}
      sla={null}
      fifo={false}
      legenda={[
        { key: 'manter', Icone: Clock, label: 'Justificar e manter' },
        { key: 'finalizar', Icone: Check, label: 'Finalizar' },
        { key: 'sms-enviado', Icone: MessageSquare, label: 'SMS Enviado' },
        { key: 'cpf-cnpj-divergente', Icone: AlertTriangle, label: 'CPF/CNPJ Divergente' },
        { key: 'pedido-complementar', Icone: PackagePlus, label: 'Pedido Complementar' },
        { key: 'analisar', Icone: Eye, label: 'Analisar' },
        { key: 'cancelar', Icone: X, label: 'Cancelar' },
      ]}
      isLoading={isLoading}
      onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
      vazio={
        itens.length === 0
          ? { titulo: 'Nenhum item pendente', descricao: 'Todos os itens foram processados' }
          : undefined
      }
    >
            <table className="w-full" style={{ tableLayout: 'fixed', minWidth: TRIAGEM_LARGURA_MINIMA_PADRAO }}>
              <TriagemStandardColGroup extraCols={<><col style={{ width: '16%' }} /><col style={{ width: '326px' }} /></>} />
              <thead className="sticky top-0 z-10 bg-surface">
                <tr>
                  <TriagemStandardHeaders rotuloDocumento={rotuloSegmento(segmento)} />
                  <th className={TRIAGEM_TH}>Justificativa</th>
                  <th className={TRIAGEM_TH}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => (
                  <TriagemAtivacaoRow
                    key={item.item_id}
                    item={item}
                    onFinalizar={() => handleFinalizar(item)}
                    onSmsEnviado={() => rodar(item, 'sms', () => moverParaSmsEnviado(item.item_id))}
                    onCpfCnpjDivergente={() => rodar(item, 'divergente', () => moverParaCpfCnpjDivergente(item.item_id))}
                    onPedidoComplementar={() => setComplementar({
                      pedidoId: item.pedido_id, numeroPedido: item.numero_pedido, razaoSocial: item.razao_social,
                      cotacao: item.numero_cotacao_operadora, colunaOrigem: 'AGUARDANDO NOTA FISCAL', origem: 'Triagem Nota Fiscal / Bilhete',
                      linhas: linhasDaCotacao(itensTodos, item, (i) => i.linhas_formatadas[0] || ''),
                    })}
                    onMoverParaFinal={() => rodar(item, 'manter', () => moverParaFinal(item.item_id))}
                    onAfterCancel={() => { void refetch(); }}
                    isFinalizando={ocupada(chave(item, 'finalizar'))}
                    isMovingToAguardandoNF={false}
                    isMovingToSmsEnviado={ocupada(chave(item, 'sms'))}
                    isMovingToCpfCnpjDivergente={ocupada(chave(item, 'divergente'))}
                    isMovingToFinal={ocupada(chave(item, 'manter'))}
                    showAguardarNF={false}
                    exigeJustificativa
                  />
                ))}
              </tbody>
            </table>
    </TriagemShell>
    <NumeroLinhaHaDialog {...numeroHaDialog} />
    <PedidoComplementarDialog alvo={complementar} onClose={() => setComplementar(null)} />
    </>
  );
}
